import { useRef, useEffect, useState } from 'react';
import PropTypes from 'prop-types';

/**
 * DescriptionInput Component
 * Shared textarea for image descriptions
 *
 * Responsibility: Provide a consistent description input UI
 * Used by: ImageTile, UploadPlaceholder
 */
export default function DescriptionInput({
    value,
    defaultValue,
    onChange,
    placeholder = 'Add description...',
    disabled = false,
    overlay = false,
    overlayVariant,
    tabIndex = -1,
    onFocus,
    onBlur,
    preventZoom = false,
}) {
    const textareaRef = useRef(null);
    const [isEmpty, setIsEmpty] = useState(
        () => String(value ?? defaultValue ?? '').trim().length === 0,
    );
    const textOutlineStyle = {
        // Prefer true stroke where supported; shadow fallback for others.
        WebkitTextStroke: '1px rgba(255,255,255,0.2)',
        textShadow:
            '1px 0 0 rgba(255,255,255,0.2), -1px 0 0 rgba(255,255,255,0.2), 0 1px 0 rgba(255,255,255,0.2), 0 -1px 0 rgba(255,255,255,0.2), 1px 1px 0 rgba(255,255,255,0.2), 1px -1px 0 rgba(255,255,255,0.2), -1px 1px 0 rgba(255,255,255,0.2), -1px -1px 0 rgba(255,255,255,0.2)',
    };
    const isWhiteOnBlack = overlay && overlayVariant === 'whiteOnBlack';

    // Sync value for controlled mode. The field being typed in already holds
    // it; writing it again would move the caret.
    useEffect(() => {
        if (textareaRef.current && value !== undefined && textareaRef.current.value !== value) {
            textareaRef.current.value = value;
        }
        if (value !== undefined) {
            setIsEmpty(String(value ?? '').trim().length === 0);
        }
    }, [value]);

    // Sync defaultValue when it changes (for uncontrolled mode with external updates)
    useEffect(() => {
        if (textareaRef.current && defaultValue !== undefined && value === undefined
            && textareaRef.current.value !== defaultValue) {
            textareaRef.current.value = defaultValue;
        }
        if (value === undefined && defaultValue !== undefined) {
            setIsEmpty(String(defaultValue ?? '').trim().length === 0);
        }
    }, [defaultValue, value]);

    const handleInput = () => {
        if (textareaRef.current && onChange) {
            onChange(textareaRef.current.value);
        }
        if (textareaRef.current) {
            setIsEmpty(textareaRef.current.value.trim().length === 0);
        }
    };

    // Overlay mode: positioned absolutely over the image
    if (overlay) {
        const handleFocus = (e) => {
            onFocus?.(e);
            const el = e?.target;
            if (!el) return;
            const len = (el.value || '').length;
            try {
                el.setSelectionRange(len, len);
            } catch {
                // ignore (some browsers may throw)
            }
        };

        if (isWhiteOnBlack) {
            const visibilityClass = isEmpty
                ? 'opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 focus-within:opacity-100'
                : 'opacity-100';
            return (
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10">
                    <div className="flex items-center justify-center w-full px-2 pointer-events-auto">
                        <div className={`inline-block max-w-full bg-black px-2 py-1 rounded transition-opacity duration-200 ${visibilityClass}`}>
                            <textarea
                                tabIndex={tabIndex}
                                ref={textareaRef}
                                onChange={handleInput}
                                onFocus={handleFocus}
                                onBlur={onBlur}
                                defaultValue={defaultValue ?? value ?? ''}
                                disabled={disabled}
                                className="border-none resize-none focus:outline-none overflow-hidden bg-transparent text-white placeholder:text-gray-400 block text-center text-sm"
                                placeholder={placeholder}
                                rows={1}
                                cols={20}
                                style={{
                                    lineHeight: '1.35em',
                                    paddingTop: '0.15em',
                                    paddingBottom: '0.15em',
                                    boxSizing: 'border-box',
                                    minHeight: '1.5em',
                                    minWidth: '8ch',
                                    fieldSizing: 'content',
                                }}
                            />
                        </div>
                    </div>
                </div>
            );
        }

        return (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10">
                <div className="flex items-center justify-center w-full px-2 pointer-events-auto">
                    <textarea
                        tabIndex={tabIndex}
                        ref={textareaRef}
                        onChange={handleInput}
                        onFocus={handleFocus}
                        onBlur={onBlur}
                        defaultValue={defaultValue ?? value ?? ''}
                        disabled={disabled}
                        className="w-full border-none resize-none focus:outline-none overflow-hidden bg-transparent"
                        placeholder={placeholder}
                        style={{ 
                            ...textOutlineStyle,
                            lineHeight: '1.2em',
                            textAlign: 'center',
                            height: '3.6em', // 3 * lineHeight
                            paddingTop: '1.2em',
                            paddingBottom: '1.2em',
                            boxSizing: 'border-box',
                        }}
                    />
                </div>
            </div>
        );
    }

    // Standard mode: block element below content
    // preventZoom: use 16px font-size so iOS Safari doesn't auto-zoom on focus
    return (
        <div className="mt-2">
            <textarea
                ref={textareaRef}
                tabIndex={tabIndex !== -1 ? tabIndex : undefined}
                onChange={handleInput}
                onFocus={onFocus}
                onBlur={onBlur}
                defaultValue={defaultValue ?? value ?? ''}
                disabled={disabled}
                rows={2}
                className="resize-none w-full p-1.5 text-xs bg-white border border-gray-300 text-black placeholder:text-gray-400 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:bg-gray-100"
                placeholder={placeholder}
                style={{
                    ...textOutlineStyle,
                    ...(preventZoom ? { fontSize: '16px' } : {}),
                }}
            />
        </div>
    );
}

DescriptionInput.propTypes = {
    value: PropTypes.string,
    defaultValue: PropTypes.string,
    onChange: PropTypes.func,
    placeholder: PropTypes.string,
    disabled: PropTypes.bool,
    overlay: PropTypes.bool,
    overlayVariant: PropTypes.oneOf(['whiteOnBlack']),
    tabIndex: PropTypes.number,
    onFocus: PropTypes.func,
    onBlur: PropTypes.func,
    preventZoom: PropTypes.bool,
};
